/**
 * EPIC-046 part 2 phase E — quoted post inline card.
 *
 * Renders a compact preview of the original post below the quoter's
 * body: avatar + author byline + (optional title) + body excerpt
 * (server already trims to ~30 words) + photo-count + relative
 * timestamp. Tapping anywhere on the card deep-links to the original.
 *
 * "Unavailable" branch covers the soft-delete / admin-hidden case
 * the backend surfaces via the `unavailable` flag — the placeholder
 * keeps the timeline coherent without leaking the deleted body.
 */
"use client";

import Link from "next/link";
import { useTranslation } from "react-i18next";
import type { QuotedPost } from "@/lib/api";

interface Props {
  quoted: QuotedPost;
  /**
   * Slightly tighter spacing inside the post composer's preview where
   * the embed sits next to the input rather than inline in a feed card.
   */
  variant?: "feed" | "composer";
}

export function QuotedPostEmbed({ quoted, variant = "feed" }: Props) {
  const { t } = useTranslation();
  if (quoted.unavailable) {
    return (
      <div
        className="mt-3 rounded-md border border-dashed px-3 py-2 text-[12px] italic text-[var(--color-text3)]"
        style={{ borderColor: "var(--color-border)" }}
        aria-label={t("quotedPost.unavailable")}
      >
        {t("quotedPost.unavailable")}.
      </div>
    );
  }

  const handle = quoted.authorDisplayName ?? `@${quoted.authorUsername}`;
  const inner = (
    <div
      className={
        variant === "composer"
          ? "rounded-md border border-[var(--color-border)] bg-[var(--color-bg2)] px-3 py-2"
          : "mt-3 rounded-md border border-[var(--color-border)] bg-[var(--color-bg2)] px-3 py-2.5"
      }
    >
      <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-[var(--color-text3)]">
        <span className="font-bold text-[var(--color-text2)]">{handle}</span>
        <span>·</span>
        <span>
          {quoted.createdAt
            ? new Date(quoted.createdAt).toLocaleDateString()
            : ""}
        </span>
      </div>
      {quoted.title && (
        <p className="mt-1 text-[13px] font-bold tracking-tight text-[var(--color-text)]">
          {quoted.title}
        </p>
      )}
      {quoted.bodyExcerpt && (
        <p className="mt-1 text-[12px] text-[var(--color-text2)] line-clamp-3">
          {quoted.bodyExcerpt}
        </p>
      )}
      {quoted.photoCount > 0 && (
        <p className="mt-1 text-[10px] uppercase tracking-[0.4px] text-[var(--color-text3)]">
          📷 {quoted.photoCount}
        </p>
      )}
    </div>
  );

  if (variant === "composer") return inner;
  return (
    <Link
      href={`/posts/${quoted.id}`}
      className="block transition-colors hover:bg-[var(--color-bg3)]"
    >
      {inner}
    </Link>
  );
}
